import React, { FC, ChangeEvent, Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Grid, Tabs } from '@material-ui/core';
import { createStyles, withStyles, Theme, WithStyles } from '@material-ui/core/styles';

import { FeedKey, QueryGetSharesItemsArgs } from 'api';
import { EmptyState, LargeTab, LoadMoreButton, Queries } from 'components';
import { useCurrentUser, useClient } from 'hooks';
import { DEFAULT_SORT } from 'constants/index';
import { CELL_HEIGHT } from 'features/activity/constants';

import ShareThese from './ShareThese';
import ScheduledShares from './ScheduledShares';
import PastShares from './PastShares';
import MyPosts from './MyPosts';
import ListLoader from '../ListLoader';

const styles = (theme: Theme) =>
  createStyles({
    container: {
      minHeight: CELL_HEIGHT,
    },
    tabs: {
      borderBottom: `1px solid ${theme.custom.palette.grey3}`,
    },
  });

const SharingTabs: FC<WithStyles<typeof styles>> = ({ classes }) => {
  const [tabValue, setTabValue] = useState(0);
  const { t } = useTranslation();
  const currentUser = useCurrentUser();
  const { allow_sharing } = useClient();

  const shareThese = t('sharing:shareThese');
  const scheduledShares = t('sharing:scheduledShares');
  const pastShares = t('sharing:pastShares');
  const myPosts = t('sharing:myPosts');

  function renderShareThese() {
    return (
      <Queries.RecommendContent loader={<ListLoader />} size={10} resultsFrom={0}>
        {(data, { exhausted, loading, onLoadMore }) => {
          if (!data.results.length) {
            return <EmptyState name="shareThese" />;
          }

          return (
            <Fragment>
              <ShareThese results={data.results} />
              {!exhausted && <LoadMoreButton loading={loading} onClick={onLoadMore} />}
            </Fragment>
          );
        }}
      </Queries.RecommendContent>
    );
  }

  function renderScheduledShares() {
    const variables: QueryGetSharesItemsArgs = {
      scheduled: true,
      group_by_session: true,
      data: {
        limit: 10,
      },
    };

    return (
      <Queries.GetSharesItems loader={<ListLoader />} variables={variables}>
        {(data, { exhausted, loading, onLoadMore }) => {
          const { items_by_session } = data;

          if (!items_by_session.length) {
            return <EmptyState name="scheduled" />;
          }

          return (
            <Fragment>
              <ScheduledShares results={items_by_session} variables={variables} />
              {!exhausted && <LoadMoreButton loading={loading} onClick={onLoadMore} />}
            </Fragment>
          );
        }}
      </Queries.GetSharesItems>
    );
  }

  function renderPastShares() {
    const variables: QueryGetSharesItemsArgs = {
      scheduled: false,
      data: {
        limit: 10,
      },
    };

    return (
      <Queries.GetSharesItems loader={<ListLoader />} variables={variables}>
        {(data, { exhausted, loading, onLoadMore }) => {
          const { items } = data;
          if (!items.length) {
            return <EmptyState name="past" />;
          }

          return (
            <Fragment>
              <PastShares results={items} />
              {!exhausted && <LoadMoreButton loading={loading} onClick={onLoadMore} />}
            </Fragment>
          );
        }}
      </Queries.GetSharesItems>
    );
  }

  function renderMyPosts() {
    const variables = {
      sort: DEFAULT_SORT,
      key: FeedKey.User,
      id: currentUser.id,
      limit: 10,
    };

    return (
      <Queries.GetFeed loader={<ListLoader />} variables={variables}>
        {(data, { exhausted, loading, onLoadMore }) => {
          if (!data.results.length) {
            return <EmptyState name="myPosts" />;
          }
          return (
            <Fragment>
              <MyPosts results={data.results} />
              {!exhausted && <LoadMoreButton loading={loading} onClick={onLoadMore} />}
            </Fragment>
          );
        }}
      </Queries.GetFeed>
    );
  }

  function handleTabChange(event: ChangeEvent<HTMLDivElement>, value: number) {
    setTabValue(value);
  }

  if (!allow_sharing) {
    return (
      <Fragment>
        <Tabs value={tabValue} onChange={handleTabChange} className={classes.tabs}>
          <LargeTab label={myPosts} />
        </Tabs>
        <Grid container alignItems="center" justify="center" className={classes.container}>
          <Grid item xs>
            {tabValue === 0 && renderMyPosts()}
          </Grid>
        </Grid>
      </Fragment>
    );
  }
  return (
    <Fragment>
      <Tabs value={tabValue} onChange={handleTabChange} className={classes.tabs}>
        <LargeTab label={shareThese} />
        <LargeTab label={scheduledShares} />
        <LargeTab label={pastShares} />
        <LargeTab label={myPosts} />
      </Tabs>
      <Grid container alignItems="center" justify="center" className={classes.container}>
        <Grid item xs>
          {tabValue === 0 && renderShareThese()}
          {tabValue === 1 && renderScheduledShares()}
          {tabValue === 2 && renderPastShares()}
          {tabValue === 3 && renderMyPosts()}
        </Grid>
      </Grid>
    </Fragment>
  );
};

export default withStyles(styles)(SharingTabs);
